import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast, toast } from '../components/UI/Toast';
import Card from '../components/UI/Card';
import StepIndicator from '../components/Registration/StepIndicator';
import Step1FirmInfo from '../components/Registration/Step1FirmInfo';
import Step2PersonalInfo from '../components/Registration/Step2PersonalInfo';
import Step3ProfessionalInfo from '../components/Registration/Step3ProfessionalInfo';
import Step4Partners from '../components/Registration/Step4Partners';
import Step5Documents from '../components/Registration/Step5Documents';
import { RegistrationData } from '../types';
import { CheckCircle, ArrowLeft, User, Building, Phone, Mail, RefreshCw } from 'lucide-react';
import Button from '../components/UI/Button';
import { ApiService } from '../services/api';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const BackButton = styled.button<{ theme: any }>`
  background: none;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.text};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const HeaderTitle = styled.h1<{ theme: any }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 3rem 2rem;
`;

const SuccessIcon = styled.div<{ theme: any }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.success}20;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: ${props => props.theme.colors.success};
`;

const SuccessTitle = styled.h2<{ theme: any }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 1rem;
`;

const SuccessMessage = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.125rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const TrackingSection = styled.div<{ theme: any }>`
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.primary}40;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const TrackingLabel = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  margin: 0 0 0.5rem 0;
  font-weight: 500;
`;

const TrackingIdDisplay = styled.div<{ theme: any }>`
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.background};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
`;

// Registration Type Selection Components
const TypeSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
`;

const TypeSelectionTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0;
  text-align: center;
`;

const TypeSelectionSubtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1rem;
  margin: 0;
  text-align: center;
  max-width: 500px;
`;

const TypeOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 600px;
`;

const TypeOptionCard = styled.div<{ theme: any }>`
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
`;

const TypeOptionIcon = styled.div<{ theme: any; color: string }>`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${props => props.color}15;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const TypeOptionTitle = styled.h3<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const TypeOptionDescription = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.5;
`;

// Identification Input Components
const IdentificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
  max-width: 500px;
  margin: 0 auto;
`;

const IdentificationTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  text-align: center;
`;

const IdentificationSubtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  margin: 0;
  text-align: center;
  line-height: 1.5;

  a {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    text-decoration: underline;
  }
`;

const IdentificationForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 0.875rem;
  font-weight: 600;
`;

const FormInput = styled.input<{ theme: any }>`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1rem;
`;

// OTP Verification Components
const OtpContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;
  max-width: 500px;
  margin: 0 auto;
  text-align: center;
`;

const OtpIcon = styled.div<{ theme: any; color: string }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.color}15;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
`;

const OtpTitle = styled.h2<{ theme: any }>`
  color: ${props => props.theme.colors.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`;

const OtpSubtitle = styled.p<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.95rem;
  margin: 0;
  line-height: 1.5;
`;

const OtpSentTo = styled.div<{ theme: any }>`
  background: ${props => props.theme.colors.background};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 12px;
  padding: 1rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const OtpInputContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin: 1.5rem 0;
`;

const OtpInput = styled.input<{ theme: any }>`
  width: 50px;
  height: 50px;
  border-radius: 10px;
  border: 2px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  outline: none;
  transition: all 0.2s ease;
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
  
  &:invalid {
    border-color: #dc2626;
  }
`;

const OtpActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ResendContainer = styled.div<{ theme: any }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const ResendButton = styled.button<{ theme: any; disabled: boolean }>`
  background: none;
  border: none;
  color: ${props => props.disabled ? props.theme.colors.textSecondary : props.theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-decoration: underline;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.primary}CC;
  }
  
  &:disabled {
    opacity: 0.6;
  }
`;

const CountdownText = styled.span<{ theme: any }>`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.875rem;
`;

const RegistrationPage: React.FC = () => {
  const { theme } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationFlow, setRegistrationFlow] = useState<'type-selection' | 'identification' | 'otp-verification' | 'registration'>('type-selection');
  const [registrationType, setRegistrationType] = useState<'individual' | 'corporate' | null>(null);
  const [identificationNumber, setIdentificationNumber] = useState('');
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [contactInfo, setContactInfo] = useState({ phone: '', email: '' });
  const [taxLookupSessionId, setTaxLookupSessionId] = useState<string | null>(null);
  const [isVerifyingTaxId, setIsVerifyingTaxId] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  usePageTitle('Registration');
  const [registrationData, setRegistrationData] = useState<Partial<RegistrationData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedSummary, setSubmittedSummary] = useState<null | {
    taxId?: string | null;
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    firmName?: string | null;
  }>(null);

  const stepTitles = [
    'Firm Information',
    'Personal Information', 
    'Professional Information',
    'Partners Information',
    'Document Upload'
  ];

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleNext = (stepData: Partial<RegistrationData>) => {
    const updatedData = { ...registrationData, ...stepData };
    setRegistrationData(updatedData);

    if (currentStep === 5) {
      // Final step - submit registration
      handleSubmitRegistration(updatedData);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleBack = () => {
    if (registrationFlow === 'identification') {
      setRegistrationFlow('type-selection');
      setRegistrationType(null);
    } else if (registrationFlow === 'otp-verification') {
      setRegistrationFlow('identification');
      setOtpValues(['', '', '', '', '', '']);
      setTaxLookupSessionId(null);
    } else if (registrationFlow === 'registration') {
      setRegistrationFlow('otp-verification');
      setCurrentStep(1);
    } else {
      navigate('/');
    }
  };

  // New handlers for registration flow
  const handleTypeSelection = (type: 'individual' | 'corporate') => {
    setRegistrationType(type);
    setRegistrationFlow('identification');
  };

  const formatTaxIdInput = (value: string) => {
    // 13-digit numeric code
    return value.replace(/\D/g, '').slice(0, 13);
  };

  const handleIdentificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!registrationType) return;
    
    if (!identificationNumber.trim()) {
      showToast(toast.error(
        'Required Field',
        'Please enter your Tax ID'
      ));
      return;
    }

    if (!/^\d{13}$/.test(identificationNumber)) {
      showToast(toast.error(
        'Invalid Format',
        'Tax ID must be a 13-digit code'
      ));
      return;
    }

    try {
      setIsVerifyingTaxId(true);

      // Store Tax ID in registration data
      setRegistrationData(prev => ({
        ...prev,
        tin: identificationNumber,
      }));

      const lookup = await ApiService.lookupTaxId(registrationType, identificationNumber);
      setTaxLookupSessionId(lookup.sessionId);

      // The backend returns emailMasked; use it directly in the OTP screen.
      setContactInfo({ phone: '', email: lookup.emailMasked });

      // Start resend countdown
      setResendCountdown(60);

      // Move to OTP verification
      setRegistrationFlow('otp-verification');
    } catch (err: any) {
      if (err?.code === 'CONSULTANT_EXISTS' || (err?.message || '').includes('A consultant with this Tax ID already exists')) {
        showToast(toast.error(
          'Tax Consultant Already Exists',
          'A tax consultant with this Tax ID already exists. Please login to continue, or track your registration.'
        ));
        return;
      }

      showToast(toast.error(
        'Tax ID Verification Failed',
        err?.message || 'Unable to verify Tax ID. Please try again.'
      ));
    } finally {
      setIsVerifyingTaxId(false);
    }
  };

  // OTP handlers
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpCode = otpValues.join('');
    if (otpCode.length !== 6) {
      showToast(toast.error(
        'Invalid OTP',
        'Please enter the complete 6-digit OTP code'
      ));
      return;
    }

    if (!registrationType || !taxLookupSessionId) {
      showToast(toast.error(
        'Session Expired',
        'Please go back and verify your Tax ID again.'
      ));
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const result = await ApiService.verifyOtp(registrationType, taxLookupSessionId, otpCode);

      if (!result?.ok) {
        throw new Error(result?.error || 'OTP verification failed');
      }

      showToast(toast.success(
        'OTP Verified',
        'Your email has been verified successfully'
      ));

      // Persist sessionId and taxId for subsequent save/submit/upload calls
      sessionStorage.setItem('registrationSessionId', taxLookupSessionId);
      sessionStorage.setItem('registrationType', registrationType);
      sessionStorage.setItem('registrationTaxId', identificationNumber);

      // Prefill consultant registration using taxIdLookup payload
      const lookup = result.taxIdLookup || {};
      setRegistrationData(prev => ({
        ...prev,
        firm_name: lookup.name ?? prev.firm_name,
        address: lookup.businessAddressDetails ?? prev.address,
        email_address: lookup.email ?? prev.email_address,
        phone_number: (() => {
          const raw = (lookup.phone ?? prev.phone_number ?? '') as string;
          const digits = raw.replace(/\D/g, '');
          if (digits.startsWith('234') && digits.length === 13) return `0${digits.slice(3)}`;
          if (digits.startsWith('234') && digits.length === 14) return `0${digits.slice(3, 13)}`;
          if (digits.startsWith('0') && digits.length === 11) return digits;
          if (digits.length === 11) return digits;
          // fallback: keep what we had
          return prev.phone_number;
        })(),
        tin: (lookup.taxId ?? lookup.tin) ?? prev.tin,
      }));

      // Move to main registration flow
      setRegistrationFlow('registration');
    } catch (err: any) {
      showToast(toast.error(
        'OTP Verification Failed',
        err?.message || 'Unable to verify OTP. Please try again.'
      ));
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResendOtp = () => {
    if (resendCountdown > 0) return;
    
    // Reset OTP values
    setOtpValues(['', '', '', '', '', '']);
    
    // Start countdown
    setResendCountdown(60);
    
    // Show success message
    const contactMethod = 'email address';
    showToast(toast.success(
      'OTP Resent',
      `A new OTP code has been sent to your ${contactMethod}`
    ));
  };

  // Helper function to mask contact information
  const maskContact = (contact: string, type: 'phone' | 'email') => {
    if (type === 'phone') {
      // Show first 3 and last 2 digits: 080****78
      return contact.slice(0, 3) + '****' + contact.slice(-2);
    } else {
      // If already masked from backend (e.g. bu***@example.com), display as-is.
      if (contact.includes('***')) return contact;
      // Show first 3 characters and last 6 characters: inf***any.com
      if (contact.length <= 9) {
        // If email is too short, show first 3 and mask the rest
        return contact.slice(0, 3) + '***' + contact.slice(-3);
      }
      return contact.slice(0, 3) + '***' + contact.slice(-6);
    }
  };

  const handleSubmitRegistration = async (data: Partial<RegistrationData>) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Validate that we have all required data
      if (!data.firm_name || !data.license_number || !data.phone_number || 
          !data.email_address || !data.address || !data.cac_number || 
          !data.years_in_practice || 
          !data.professional_body || !data.membership_number || 
          !data.partners || data.partners.length === 0) {
        throw new Error('Please complete all required fields');
      }

      console.log('Submitting registration data:', data);

      if (!registrationType) {
        throw new Error('Registration type missing. Please restart registration.');
      }
      const sessionId = sessionStorage.getItem('registrationSessionId');
      if (!sessionId) {
        throw new Error('Session expired. Please restart registration.');
      }

      const toE164 = (v: string) => {
        const digits = (v || '').replace(/\D/g, '');
        if (digits.startsWith('234') && digits.length === 13) return `+${digits}`;
        if (digits.startsWith('0') && digits.length === 11) return `+234${digits.slice(1)}`;
        return digits.startsWith('+') ? digits : `+${digits}`;
      };

      const payload = {
        sessionId,
        email: data.email_address,
        phone: toE164(data.phone_number),
        professionalLicenseNo: data.license_number,
        yearsInPractice: Number(data.years_in_practice),
        professionalBody: data.professional_body,
        membershipNo: data.membership_number,
        businessAddr: data.address,
        firmName: data.firm_name,
        cacNumber: data.cac_number,
        partners: (data.partners || []).map((p) => ({
          membershipNo: p.membership_number,
          email: p.email,
          phone: toE164(p.phone_number),
          yearsInPractice: Number(p.years_in_practice),
          professionalBody: p.professional_body,
        })),
        // documents are optional for now
      };

      const resp = await ApiService.submitRegistration(registrationType, payload as any);
      setSubmittedSummary({
        taxId: (resp as any)?.summary?.taxId ?? null,
        name: (resp as any)?.summary?.name ?? null,
        email: (resp as any)?.summary?.email ?? null,
        phone: (resp as any)?.summary?.phone ?? null,
        firmName: (resp as any)?.summary?.firmName ?? null,
      });

      showToast(toast.success(
        'Registration Submitted',
        'Your registration has been submitted. You will be contacted when it is approved.'
      ));

      setIsSubmitted(true);
    } catch (error) {
      console.error('Registration failed:', error);
      
      // Handle different error types and ensure user-friendly messages
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error instanceof Error) {
        // Check for specific error patterns and replace with user-friendly messages
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Registration Failed. Please check your internet connection and try again.';
        } else if (error.message.includes('NetworkError')) {
          errorMessage = 'Registration Failed. Network error occurred. Please try again.';
        } else if (error.message.includes('timeout') || error.name === 'AbortError') {
          errorMessage = 'Registration Failed. The request is taking too long. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setSubmitError(errorMessage);
      
      // Show error notification
      showToast(toast.error(
        'Registration Failed',
        errorMessage
      ));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturnHome = () => {
    navigate('/');
  };

  // Render functions for new screens
  const renderTypeSelection = () => (
    <Card>
      <TypeSelectionContainer>
        <TypeSelectionTitle theme={theme}>
          Choose Registration Type
        </TypeSelectionTitle>
        <TypeSelectionSubtitle theme={theme}>
          Select the type of registration that applies to your tax consultancy practice
        </TypeSelectionSubtitle>
        
        <TypeOptionsGrid>
          <TypeOptionCard theme={theme} onClick={() => handleTypeSelection('individual')}>
            <TypeOptionIcon theme={theme} color="#3b82f6">
              <User size={32} />
            </TypeOptionIcon>
            <TypeOptionTitle theme={theme}>Individual Practice</TypeOptionTitle>
            <TypeOptionDescription theme={theme}>
              For individual tax consultants operating as sole practitioners
            </TypeOptionDescription>
          </TypeOptionCard>
          
          <TypeOptionCard theme={theme} onClick={() => handleTypeSelection('corporate')}>
            <TypeOptionIcon theme={theme} color="#10b981">
              <Building size={32} />
            </TypeOptionIcon>
            <TypeOptionTitle theme={theme}>Corporate Practice</TypeOptionTitle>
            <TypeOptionDescription theme={theme}>
              For tax consultancy firms registered as corporate entities
            </TypeOptionDescription>
          </TypeOptionCard>
        </TypeOptionsGrid>
      </TypeSelectionContainer>
    </Card>
  );

  const renderIdentificationInput = () => (
    <Card>
      <IdentificationContainer>
        <IdentificationTitle theme={theme}>
          {registrationType === 'individual' ? 'Enter Your Tax ID' : 'Enter Your Tax ID'}
        </IdentificationTitle>
        <IdentificationSubtitle theme={theme}>
          {registrationType === 'individual' 
            ? (
              <>
                Please provide your Tax ID to verify your identity. If you don’t have a Tax ID, visit{' '}
                <a href="https://taxid.jtb.gov.ng/" target="_blank" rel="noreferrer">
                  taxid.jtb.gov.ng
                </a>{' '}
                to get one.
              </>
            )
            : (
              <>
                Please provide your Tax ID to verify your identity. If you don’t have a Tax ID, visit{' '}
                <a href="https://taxid.jtb.gov.ng/" target="_blank" rel="noreferrer">
                  taxid.jtb.gov.ng
                </a>{' '}
                to get one.
              </>
            )
          }
        </IdentificationSubtitle>
        
        <IdentificationForm onSubmit={handleIdentificationSubmit}>
          <FormGroup>
            <FormLabel theme={theme}>Tax ID</FormLabel>
            <FormInput
              theme={theme}
              type="text"
              value={identificationNumber}
              onChange={(e) => setIdentificationNumber(formatTaxIdInput(e.target.value))}
              placeholder="Enter Tax ID (13 digits)"
              maxLength={13}
              inputMode="numeric"
              autoComplete="off"
              required
            />
          </FormGroup>
          
          <FormActions>
            <Button variant="outline" type="button" onClick={handleBack}>
              Back
            </Button>
            <Button type="submit" disabled={isVerifyingTaxId}>
              {isVerifyingTaxId ? 'Verifying…' : 'Continue'}
            </Button>
          </FormActions>
        </IdentificationForm>
      </IdentificationContainer>
    </Card>
  );

  const renderOtpVerification = () => {
    // OTP verification is via Email (for individual and corporate)
    const isEmail = true;
    const contact = contactInfo.email;
    const maskedContact = maskContact(contact, 'email');
    
    return (
      <Card>
        <OtpContainer>
          <OtpIcon theme={theme} color={isEmail ? '#3b82f6' : '#10b981'}>
            {isEmail ? <Mail size={32} /> : <Phone size={32} />}
          </OtpIcon>
          
          <OtpTitle theme={theme}>
            Verify Your Email Address
          </OtpTitle>
          
          <OtpSubtitle theme={theme}>
            We've sent a 6-digit verification code to your email address.
            Please enter the code below to continue.
          </OtpSubtitle>
          
          <OtpSentTo theme={theme}>
            <Mail size={16} />
            {maskedContact}
          </OtpSentTo>
          
          <form onSubmit={handleOtpSubmit}>
            <OtpInputContainer>
              {otpValues.map((value, index) => (
                <OtpInput
                  key={index}
                  ref={(el) => { otpRefs.current[index] = el; }}
                  theme={theme}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]"
                  maxLength={1}
                  value={value}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  required
                />
              ))}
            </OtpInputContainer>
            
            <OtpActions>
              <FormActions>
                <Button variant="outline" type="button" onClick={handleBack}>
                  Back
                </Button>
                <Button type="submit" disabled={isVerifyingOtp}>
                  {isVerifyingOtp ? 'Verifying…' : 'Verify & Continue'}
                </Button>
              </FormActions>
              
              <ResendContainer theme={theme}>
                Didn't receive the code?
                {resendCountdown > 0 ? (
                  <CountdownText theme={theme}>
                    Resend in {resendCountdown}s
                  </CountdownText>
                ) : (
                  <ResendButton
                    theme={theme}
                    type="button"
                    disabled={resendCountdown > 0}
                    onClick={handleResendOtp}
                  >
                    <RefreshCw size={14} />
                    Resend Code
                  </ResendButton>
                )}
              </ResendContainer>
            </OtpActions>
          </form>
        </OtpContainer>
      </Card>
    );
  };

  const renderCurrentStep = () => {
    const stepProps = {
      data: registrationData,
      onNext: handleNext,
      onPrevious: handlePrevious,
      isLastStep: currentStep === 5,
      isSubmitting,
      submitError,
    };

    switch (currentStep) {
      case 1:
        return <Step1FirmInfo {...stepProps} />;
      case 2:
        return <Step2PersonalInfo {...stepProps} />;
      case 3:
        return <Step3ProfessionalInfo {...stepProps} />;
      case 4:
        return <Step4Partners {...stepProps} />;
      case 5:
        return <Step5Documents {...stepProps} />;
      default:
        return <Step1FirmInfo {...stepProps} />;
    }
  };

  const submittedTypeLabel = (() => {
    const t = registrationType || (sessionStorage.getItem('registrationType') as any);
    return t === 'corporate' ? 'Corporate' : t === 'individual' ? 'Individual' : null;
  })();

  if (isSubmitted) {
    return (
      <Container>
        <Card maxWidth="600px">
          <SuccessContainer>
            <SuccessIcon theme={theme}>
              <CheckCircle size={48} />
            </SuccessIcon>
            <SuccessTitle theme={theme}>Registration Submitted</SuccessTitle>
            <SuccessMessage theme={theme}>
              Thank you for registering with NRS Tax Consultant Portal.
              {submittedTypeLabel ? ` Your ${submittedTypeLabel.toLowerCase()} registration has been submitted successfully.` : ' Your registration has been submitted successfully.'}
              <br /><br />
              You will be contacted when your registration is approved.
            </SuccessMessage>

            {submittedSummary && (
              <TrackingSection theme={theme}>
                <TrackingLabel theme={theme}>
                  Submission Details
                </TrackingLabel>
                {submittedSummary.taxId && (
                  <TrackingIdDisplay theme={theme}>
                    Tax ID: {submittedSummary.taxId}
                  </TrackingIdDisplay>
                )}
                {submittedSummary.name && (
                  <TrackingLabel theme={theme} style={{ marginTop: '0.75rem' }}>
                    Name: {submittedSummary.name}
                  </TrackingLabel>
                )}
                {submittedSummary.email && (
                  <TrackingLabel theme={theme} style={{ marginTop: '0.75rem' }}>
                    Email: {submittedSummary.email}
                  </TrackingLabel>
                )}
                {submittedSummary.phone && (
                  <TrackingLabel theme={theme} style={{ marginTop: '0.75rem' }}>
                    Phone: {submittedSummary.phone}
                  </TrackingLabel>
                )}
                {submittedSummary.firmName && (
                  <TrackingLabel theme={theme} style={{ marginTop: '0.75rem' }}>
                    Firm Name: {submittedSummary.firmName}
                  </TrackingLabel>
                )}
              </TrackingSection>
            )}
            
            <Button onClick={handleReturnHome} size="large">
              Return to Home
            </Button>
          </SuccessContainer>
        </Card>
      </Container>
    );
  }

  // Render different flows based on current state
  if (registrationFlow === 'type-selection') {
    return (
      <Container>
        <Header>
          <BackButton theme={theme} onClick={handleBack}>
            <ArrowLeft size={20} />
          </BackButton>
          <HeaderTitle theme={theme}>Tax Consultant Registration</HeaderTitle>
        </Header>
        {renderTypeSelection()}
      </Container>
    );
  }

  if (registrationFlow === 'identification') {
    return (
      <Container>
        <Header>
          <BackButton theme={theme} onClick={handleBack}>
            <ArrowLeft size={20} />
          </BackButton>
          <HeaderTitle theme={theme}>
            {registrationType === 'individual' ? 'Individual Registration' : 'Corporate Registration'}
          </HeaderTitle>
        </Header>
        {renderIdentificationInput()}
      </Container>
    );
  }

  if (registrationFlow === 'otp-verification') {
    return (
      <Container>
        <Header>
          <BackButton theme={theme} onClick={handleBack}>
            <ArrowLeft size={20} />
          </BackButton>
          <HeaderTitle theme={theme}>
            Verify Email Address
          </HeaderTitle>
        </Header>
        {renderOtpVerification()}
      </Container>
    );
  }

  // Main registration flow
  return (
    <Container>
      <Header>
        <BackButton theme={theme} onClick={handleBack}>
          <ArrowLeft size={20} />
        </BackButton>
        <HeaderTitle theme={theme}>
          {registrationType === 'individual' ? 'Individual' : 'Corporate'} Tax Consultant Registration
        </HeaderTitle>
      </Header>

      <StepIndicator
        currentStep={currentStep}
        totalSteps={5}
        stepTitles={stepTitles}
      />

      <Card maxWidth="800px" padding="3rem">
        {renderCurrentStep()}
      </Card>
    </Container>
  );
};

export default RegistrationPage;
